import React, { useEffect, useRef, useState } from 'react';
import useResponsive from '../utils/useResponsive';
import { formatearMoneda } from '../utils/formatMoney';
import { DropdownCheckbox } from './ReportesCrmHome';

// Histórico de casos firmados (tabla historico_firmados). Solo lo ve el
// usuario con acceso_historico, y solo sus propios registros (lo garantiza el
// backend: GET /api/historico-firmados). Filtros y totales se calculan en el
// servidor; los revertidos se listan con alerta pero no cuentan en totales.
const API = '/api';
const TAMANO_PAGINA = 50;
const VACIO = '—';

const money = (v) => (v === null || v === undefined || v === '' ? VACIO : `$${formatearMoneda(Math.round(Number(v) || 0))}`);
const fechaDMA = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : VACIO);
const tasaFmt = (v) => (v === null || v === undefined || v === '' ? VACIO : `${Number(v).toFixed(2)}%`);
const texto = (v) => (v === null || v === undefined || v === '' ? VACIO : v);
// Nombres llegan en MAYÚSCULAS (Integral) o en Mayúsculas y minúsculas (VTC).
const tituloCase = (s) => (s || '').toLowerCase().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());
const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

const COLUMNAS = [
  { label: 'Nombre del cliente' }, { label: 'Teléfono' }, { label: 'Banco' }, { label: 'Tipo de crédito' }, { label: 'Esquema de crédito' },
  { label: 'Tasa', num: true }, { label: 'Plazo (años)', num: true }, { label: 'Crédito firmado', num: true },
  { label: 'Valor del inmueble', num: true }, { label: 'Fecha de firma' },
];

const SeguimientoHistorico = ({ onVolver, onVerHipotecas, onVerReportes, onVerNotificaciones }) => {
  const { isMobile } = useResponsive();

  // Mide el alto real de la barra azul (sticky) para que el encabezado de la
  // tabla se pegue justo debajo al hacer scroll (mismo patrón que Reportes y
  // el listado de Hipotecas con --app-header-h).
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(64);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const medir = () => setHeaderH(el.offsetHeight);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener('resize', medir);
    return () => { ro.disconnect(); window.removeEventListener('resize', medir); };
  }, [isMobile]);
  const [opciones, setOpciones] = useState({ bancos: [], tipos: [] });
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [bancos, setBancos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tasaMin, setTasaMin] = useState('');
  const [tasaMax, setTasaMax] = useState('');
  const [soloSinTasa, setSoloSinTasa] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/historico-firmados/opciones`)
      .then(r => r.json())
      .then(d => { if (!d.error) setOpciones({ bancos: d.bancos || [], tipos: d.tipos || [] }); })
      .catch(() => {});
  }, []);

  // Cualquier cambio de filtro regresa a la página 1.
  useEffect(() => { setPagina(1); }, [desde, hasta, bancos, tipos, tasaMin, tasaMax, soloSinTasa]);

  // Filtros actuales como query string (los usa la tabla y la exportación).
  const paramsFiltros = () => {
    const params = new URLSearchParams();
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    bancos.forEach(b => params.append('bancos', b));
    tipos.forEach(t => params.append('tipos', t));
    if (tasaMin !== '') params.set('tasa_min', tasaMin);
    if (tasaMax !== '') params.set('tasa_max', tasaMax);
    if (soloSinTasa) params.set('solo_sin_tasa', '1');
    return params;
  };

  useEffect(() => {
    const params = paramsFiltros();
    params.set('pagina', String(pagina));
    params.set('tamano', String(TAMANO_PAGINA));
    // Espera breve para no consultar en cada tecla de los campos de tasa.
    const t = setTimeout(() => {
      setLoading(true);
      fetch(`${API}/historico-firmados?${params.toString()}`)
        .then(r => r.json())
        .then(d => { if (d.error) { setError(d.error); setDatos(null); } else { setError(''); setDatos(d); } })
        .catch(() => setError('No se pudo cargar el Histórico. Intente de nuevo.'))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [desde, hasta, bancos, tipos, tasaMin, tasaMax, soloSinTasa, pagina]); // eslint-disable-line react-hooks/exhaustive-deps

  const hayFiltros = desde || hasta || bancos.length || tipos.length || tasaMin !== '' || tasaMax !== '' || soloSinTasa;
  const limpiar = () => {
    setDesde(''); setHasta(''); setBancos([]); setTipos([]); setTasaMin(''); setTasaMax(''); setSoloSinTasa(false);
  };

  // Exporta todo lo filtrado (no solo la página visible) a PDF o Excel.
  const [exportando, setExportando] = useState('');
  const exportar = async (formato) => {
    const params = paramsFiltros();
    params.set('formato', formato);
    setExportando(formato);
    try {
      const r = await fetch(`${API}/historico-firmados/exportar?${params.toString()}`);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.error || 'No se pudo exportar el Histórico. Intente de nuevo.');
        return;
      }
      const blob = await r.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `historico-firmados.${formato}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      alert('No se pudo exportar el Histórico. Intente de nuevo.');
    } finally {
      setExportando('');
    }
  };

  const st = {
    container: { padding: isMobile ? 12 : 24, maxWidth: 1500, margin: '0 auto' },
    // Mismo marco que Reportes/Notificaciones (sin overflow:hidden para no romper el sticky).
    frame: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
    frameHeader: {
      backgroundColor: '#1a3a6e', color: 'white', padding: isMobile ? '14px 16px' : '16px 22px',
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      position: 'sticky', top: 'var(--app-header-h, 120px)', zIndex: 31,
      borderRadius: '12px 12px 0 0',
    },
    backBtn: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.28)', color: 'white', fontSize: 12.5, fontWeight: 'bold', borderRadius: 7, padding: '7px 12px', cursor: 'pointer' },
    title: { margin: 0, fontSize: 17, fontWeight: 'bold' },
    panel: { padding: isMobile ? 14 : '20px 22px 26px' },
    filters: { display: 'flex', gap: isMobile ? 12 : 18, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 },
    field: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: 11, fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 },
    input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit' },
    rango: { display: 'flex', alignItems: 'center', gap: 6 },
    exportBtn: (color) => ({ border: 'none', backgroundColor: color, color: 'white', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }),
    limpiarBtn: { border: '1px solid #d1d5db', backgroundColor: 'white', color: '#1a3a6e', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 'bold', cursor: 'pointer' },
    totales: { display: 'flex', gap: isMobile ? 10 : 22, flexWrap: 'wrap', alignItems: 'center', backgroundColor: '#f0f4fb', border: '1px solid #d6e0f0', borderRadius: 10, padding: '10px 16px', marginBottom: 12 },
    totalItem: { display: 'flex', flexDirection: 'column' },
    totalLbl: { fontSize: 10.5, fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 },
    totalVal: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', fontVariantNumeric: 'tabular-nums' },
    nota: { fontSize: 12, color: '#93590a' },
    aviso: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', backgroundColor: '#fff7e6', border: '1px solid #f5d9a8', color: '#7a4a05', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 12 },
    linkBtn: { background: 'none', border: 'none', color: '#1a3a6e', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer', fontSize: 12.5, padding: 0 },
    // Sin overflow (ni en celular): un overflow distinto de "visible" rompe el
    // sticky del encabezado de la tabla. En celular la tabla se recorre a lo
    // ancho con el scroll de la página, igual que el listado de Hipotecas.
    tableScroll: { border: '1px solid #e5e7eb', borderRadius: 10 },
    table: { width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' },
    // Sticky: la línea inferior va como sombra porque con border-collapse el
    // borde no acompaña a la celda pegada.
    th: {
      textAlign: 'left', fontSize: 10.5, fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3,
      backgroundColor: '#f9fafb', padding: '6px 10px', whiteSpace: 'nowrap', boxShadow: 'inset 0 -1px 0 #e5e7eb',
      position: 'sticky', top: `calc(var(--app-header-h, 120px) + ${headerH}px)`, zIndex: 2,
    },
    td: { padding: '5px 10px', borderBottom: '1px solid #e5e7eb', fontSize: 12.5, whiteSpace: 'nowrap' },
    num: { textAlign: 'right' },
    empty: { padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 },
    revertidoRow: { backgroundColor: '#fdf0f0', color: '#7f1d1d' },
    revertidoPill: { display: 'inline-block', marginLeft: 8, fontSize: 10.5, fontWeight: 'bold', color: '#b91c1c', backgroundColor: '#fde2e2', border: '1px solid #f5b5b5', borderRadius: 999, padding: '1px 8px' },
    paginacion: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 12, fontSize: 12.5, color: '#4b5563' },
    pagBtn: (disabled) => ({ border: '1px solid #d1d5db', backgroundColor: disabled ? '#f3f4f6' : 'white', color: disabled ? '#9ca3af' : '#1a3a6e', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 'bold', cursor: disabled ? 'default' : 'pointer' }),
  };

  const filas = datos?.filas || [];
  const totalPaginas = datos?.total_paginas || 1;
  const primera = datos && datos.total_filas ? (datos.pagina - 1) * datos.tamano + 1 : 0;
  const ultima = datos ? Math.min(datos.pagina * datos.tamano, datos.total_filas) : 0;

  return (
    <div style={st.container}>
      <div style={st.frame}>
        <div ref={headerRef} style={st.frameHeader}>
          <button style={st.backBtn} onClick={onVolver}>← Dashboard</button>
          {onVerHipotecas && <button style={st.backBtn} onClick={onVerHipotecas}>🏦 Hipotecas</button>}
          {onVerReportes && <button style={st.backBtn} onClick={onVerReportes}>📊 Reportes</button>}
          {onVerNotificaciones && <button style={st.backBtn} onClick={onVerNotificaciones}>🔔 Notificaciones</button>}
          <h2 style={st.title}>📜 Histórico de Firmados</h2>
        </div>

        <div style={st.panel}>
          <div style={st.filters}>
            <div style={st.field}>
              <label style={st.label}>Fecha de firma</label>
              <div style={st.rango}>
                <input type="date" style={st.input} value={desde} max={hasta || undefined} onChange={e => setDesde(e.target.value)} aria-label="Desde" />
                <span style={{ color: '#6b7280' }}>a</span>
                <input type="date" style={st.input} value={hasta} min={desde || undefined} onChange={e => setHasta(e.target.value)} aria-label="Hasta" />
              </div>
            </div>
            <DropdownCheckbox label="Banco" opciones={opciones.bancos.map(b => ({ v: b, l: b }))} seleccion={bancos}
              onToggle={v => toggle(bancos, setBancos, v)} onClear={() => setBancos([])} ancho={170} />
            <DropdownCheckbox label="Tipo de crédito" opciones={opciones.tipos.map(t => ({ v: t, l: t }))} seleccion={tipos}
              onToggle={v => toggle(tipos, setTipos, v)} onClear={() => setTipos([])} ancho={250} />
            <div style={st.field}>
              <label style={st.label}>Tasa (%)</label>
              <div style={st.rango}>
                <input type="number" step="0.01" min="0" placeholder="Mín." style={{ ...st.input, width: 80 }} value={tasaMin}
                  disabled={soloSinTasa} onChange={e => setTasaMin(e.target.value)} aria-label="Tasa mínima" />
                <span style={{ color: '#6b7280' }}>a</span>
                <input type="number" step="0.01" min="0" placeholder="Máx." style={{ ...st.input, width: 80 }} value={tasaMax}
                  disabled={soloSinTasa} onChange={e => setTasaMax(e.target.value)} aria-label="Tasa máxima" />
              </div>
            </div>
            <button style={{ ...st.limpiarBtn, opacity: hayFiltros ? 1 : 0.55 }} onClick={limpiar} disabled={!hayFiltros}>✕ Limpiar filtros</button>
            <button style={{ ...st.exportBtn('#b91c1c'), opacity: exportando || !datos?.total_filas ? 0.55 : 1 }}
              onClick={() => exportar('pdf')} disabled={!!exportando || !datos?.total_filas}>
              {exportando === 'pdf' ? 'Generando...' : '⬇ Exportar PDF'}
            </button>
            <button style={{ ...st.exportBtn('#16794f'), opacity: exportando || !datos?.total_filas ? 0.55 : 1 }}
              onClick={() => exportar('xlsx')} disabled={!!exportando || !datos?.total_filas}>
              {exportando === 'xlsx' ? 'Generando...' : '⬇ Exportar Excel'}
            </button>
          </div>

          {datos && !soloSinTasa && datos.sin_tasa_excluidos > 0 && (
            <div style={st.aviso}>
              <span>⚠ {datos.sin_tasa_excluidos} {datos.sin_tasa_excluidos === 1 ? 'registro sin tasa no se incluye' : 'registros sin tasa no se incluyen'} con el filtro de tasa.</span>
              <button style={st.linkBtn} onClick={() => setSoloSinTasa(true)}>Ver registros sin tasa</button>
            </div>
          )}
          {soloSinTasa && (
            <div style={st.aviso}>
              <span>Mostrando solo los registros <strong>sin tasa</strong> (con los demás filtros).</span>
              <button style={st.linkBtn} onClick={() => setSoloSinTasa(false)}>Volver al filtro de tasa</button>
            </div>
          )}

          <div style={st.totales}>
            <div style={st.totalItem}>
              <span style={st.totalLbl}>Casos</span>
              <span style={st.totalVal}>{datos ? datos.totales.casos.toLocaleString('es-MX') : VACIO}</span>
            </div>
            <div style={st.totalItem}>
              <span style={st.totalLbl}>Crédito firmado</span>
              <span style={st.totalVal}>{datos ? money(datos.totales.credito_firmado) : VACIO}</span>
            </div>
            {datos && datos.revertidos > 0 && (
              <span style={st.nota}>⚠ {datos.revertidos} {datos.revertidos === 1 ? 'caso revertido en CRM no cuenta' : 'casos revertidos en CRM no cuentan'} en los totales</span>
            )}
          </div>

          {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

          <div style={st.tableScroll}>
            <table style={st.table}>
              <thead>
                <tr>{COLUMNAS.map((c, i) => (
                  <th key={c.label} style={{
                    ...st.th, ...(c.num ? st.num : {}),
                    ...(i === 0 ? { borderTopLeftRadius: 10 } : {}), ...(i === COLUMNAS.length - 1 ? { borderTopRightRadius: 10 } : {}),
                  }}>{c.label}</th>
                ))}</tr>
              </thead>
              <tbody>
                {loading && !datos && <tr><td colSpan={COLUMNAS.length} style={st.empty}>Cargando...</td></tr>}
                {datos && filas.length === 0 && (
                  <tr><td colSpan={COLUMNAS.length} style={st.empty}>{hayFiltros ? 'No hay casos con estos filtros' : 'Aún no hay casos en el Histórico'}</td></tr>
                )}
                {filas.map(f => {
                  const revertido = !!f.revertido_en;
                  return (
                    <tr key={f.id} style={revertido ? st.revertidoRow : undefined}
                      title={revertido ? `Revertido en CRM el ${fechaDMA(f.revertido_en)}: el caso dejó de estar firmado` : undefined}>
                      <td style={st.td}>
                        {f.nombre_cliente ? tituloCase(f.nombre_cliente) : VACIO}
                        {revertido && <span style={st.revertidoPill}>⚠ Revertido en CRM</span>}
                      </td>
                      <td style={st.td}>{texto(f.telefono)}</td>
                      <td style={st.td}>{texto(f.banco)}</td>
                      <td style={st.td}>{texto(f.tipo_credito)}</td>
                      <td style={st.td}>{texto(f.esquema_credito)}</td>
                      <td style={{ ...st.td, ...st.num }}>{tasaFmt(f.tasa)}</td>
                      <td style={{ ...st.td, ...st.num }}>{texto(f.plazo_anios)}</td>
                      <td style={{ ...st.td, ...st.num }}>{money(f.credito_firmado)}</td>
                      <td style={{ ...st.td, ...st.num }}>{money(f.valor_inmueble)}</td>
                      <td style={st.td}>{fechaDMA(f.fecha_firma)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {datos && datos.total_filas > 0 && (
            <div style={st.paginacion}>
              <span>Mostrando {primera.toLocaleString('es-MX')}–{ultima.toLocaleString('es-MX')} de {datos.total_filas.toLocaleString('es-MX')}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button style={st.pagBtn(pagina <= 1)} disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>← Anterior</button>
                <span>Página {pagina} de {totalPaginas}</span>
                <button style={st.pagBtn(pagina >= totalPaginas)} disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Siguiente →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeguimientoHistorico;
